import { seriesCanvasArea } from "d3fc"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { DeltaRegion, PageRectangle } from "../../../../Types/Trace"
import { D3Trace } from "./D3Trace"
import { TraceRendererOptions, TraceRenderStrategy } from "./RenderStrategy"
import { scaleLinear, ScaleTime } from "d3"
import { getGapIndexes, getTimeSeriesData, TimeSeriesData } from "../../../../Data/TimeSeriesData"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"

export class DeltaRenderStrategy extends TraceRenderStrategy {
	private directRenderer
	private offscreenRenderer
	private pageManager: TimeSeriesPageManager<ModalityPage>
	private config: DeltaRegion
	private d3Trace: D3Trace
	private renderCacheKey: string = ""

	constructor(pageManager: TimeSeriesPageManager<ModalityPage>, d3Trace: D3Trace, config: DeltaRegion) {
		super()
		this.config = config
		this.d3Trace = d3Trace
		this.pageManager = pageManager

		this.offscreenRenderer = seriesCanvasArea()
			.crossValue((p: [number, number, number]) => p[0])
			.mainValue((p: [number, number, number]) => p[1])
			.baseValue((p: [number, number, number]) => p[2])

		this.directRenderer = seriesCanvasArea()
			.crossValue((p: [number, number, number]) => p[0])
			.mainValue((p: [number, number, number]) => p[1])
			.baseValue((p: [number, number, number]) => p[2])

		this.updateRenderCacheKey()
	}

	public updateConfig(traceConfig: DeltaRegion) {
		this.config = traceConfig
		const { r, g, b } = this.config.color

		this.directRenderer
			.xScale(this.config.xScale)
			.yScale(this.config.yScale)
			.decorate((context: CanvasRenderingContext2D) => {
				context.strokeStyle = "transparent"
				context.fillStyle = `rgba(${r}, ${g}, ${b}, 0.2)`
			})

		this.offscreenRenderer.decorate((context: CanvasRenderingContext2D) => {
			context.strokeStyle = "transparent"
			context.fillStyle = `rgba(${r}, ${g}, ${b}, 0.2)`
		})

		this.updateRenderCacheKey()
	}

	public getDirectRenderer = (options?: TraceRendererOptions) => {
		if (options?.xScale) {
			this.directRenderer.xScale(options.xScale)
		}

		if (options?.yScale) {
			this.directRenderer.yScale(options.yScale)
		}

		return this.directRenderer
	}

	public getOffscreenRenderer = (options?: TraceRendererOptions) => {
		if (options?.xScale) {
			this.offscreenRenderer.xScale(options.xScale)
		}

		if (options?.yScale) {
			this.offscreenRenderer.yScale(options.yScale)
		}

		return this.offscreenRenderer
	}

	render(): void {
		this.pageManager.getPagesInView().forEach(page => this.d3Trace.renderPage(page))
	}

	public renderTimeSeriesData(first: TimeSeriesData, second: TimeSeriesData, page: ModalityPage, renderer: (drawableData: Iterable<[number, number, number]>) => void) {
		// If there is only one data point, just pretend that the value is constant.
		first = this.checkIfOnlyOneValue(first, page)
		second = this.checkIfOnlyOneValue(second, page)

		const chunks = this.drawableDataGenerator(first, second, page)

		for (const chunk of chunks) {
			renderer(chunk)
		}

		// The edges can be used to redraw the gap between pages when the underlying data is unavailable
		const edges = [
			[first.times[0], first.data[0], second.data[0]],
			[first.times[first.times.length - 1], first.data[first.data.length - 1], second.data[second.data.length - 1]],
		]

		return edges as [number | undefined, number, number][]
	}

	public getRenderCacheKey(): string {
		return this.renderCacheKey
	}

	public renderPage(
		page: ModalityPage,
		reactCallbacks: ModalityGraphGroupReactCallbacks,
		offscreenXScale: ScaleTime<any, any, any>,
		offscreenCanvas: OffscreenCanvas,
		context: CanvasRenderingContext2D,
		offscreenContext: OffscreenCanvasRenderingContext2D,
		pageRectangle: PageRectangle,
		unloadedRectangle: PageRectangle | null,
	) {
		const { x, y, width } = pageRectangle

		const firstDataObjectId = reactCallbacks.dataSourceMap.get(this.config.first.dataSource) ?? Infinity
		const firstTraceData = page.data.get(firstDataObjectId)?.get(this.config.first.dataKey)

		const secondDataObjectId = reactCallbacks.dataSourceMap.get(this.config.second.dataSource) ?? Infinity
		const secondTraceData = page.data.get(secondDataObjectId)?.get(this.config.second.dataKey)

		let edges: [number | undefined, number, number][] = []

		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		if (firstTraceData !== undefined && secondTraceData !== undefined) {
			offscreenXScale.domain([page.startTime, page.endTime]).range([0, width])
			const firstTimeSeriesData = getTimeSeriesData(firstTraceData, this.config.first)
			const secondTimeSeriesData = getTimeSeriesData(secondTraceData, this.config.second)
			edges = this.renderTimeSeriesData(firstTimeSeriesData, secondTimeSeriesData, page, this.getOffscreenRenderer({ xScale: offscreenXScale, yScale: this.config.yScale }))
		}

		const bitmap = offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(firstDataObjectId, this.renderCacheKey, { bitmap, dirty: false, edges: edges })
		context.drawImage(bitmap, x, y)
	}

	private updateRenderCacheKey = () => {
		const { graphId, first, second, color } = this.config
		const firstDataObjectId = this.d3Trace.getDataObjectId(first) ?? Infinity
		const secondDataObjectId = this.d3Trace.getDataObjectId(second) ?? Infinity

		let firstCompositeIndex = undefined
		let secondCompositeIndex = undefined

		if (first.isCompositePart) {
			firstCompositeIndex = first.compositeIndex
		}

		if (second.isCompositePart) {
			secondCompositeIndex = second.compositeIndex
		}

		this.renderCacheKey = `${graphId}-${first.dataKey}-${firstDataObjectId}-${firstCompositeIndex}-${second.dataKey}-${secondDataObjectId}-${secondCompositeIndex}-rgb(${color.r}, ${color.g}, ${color.b})`
	}

	private drawableDataGenerator(first: TimeSeriesData, second: TimeSeriesData, page: ModalityPage): [number, number, number][][] {
		const continuousChunks: [number, number, number][][] = []
		let currentChunkIndex = 0

		const firstDataObjectId = this.d3Trace.getDataObjectId(this.config.first)
		const secondDataObjectId = this.d3Trace.getDataObjectId(this.config.second)

		// Because the data can exist on separate grids, create functions that to get the value at any time
		const firstNullIndexes = first.data.map((value, index) => (value === null || Number.isNaN(value) ? index : -1)).filter(index => index > -1)
		const firstDataInterpolator = scaleLinear()
			.domain(first.times.filter((time, index) => time !== undefined && !firstNullIndexes.includes(index)) as number[])
			.range(first.data.filter((_, index) => !firstNullIndexes.includes(index)))

		const secondNullIndexes = second.data.map((value, index) => (value === null || Number.isNaN(value) ? index : -1)).filter(index => index > -1)
		const secondDataInterpolator = scaleLinear()
			.domain(second.times.filter((time, index) => time !== undefined && !secondNullIndexes.includes(index)) as number[])
			.range(second.data.filter((_, index) => !secondNullIndexes.includes(index)))

		if (firstDataObjectId === undefined || secondDataObjectId === undefined) {
			throw new Error("One of the data object IDs was not defined.")
		}

		// Store the gaps of each time series so that we don't accidentally fill in the gaps
		const firstTimingInfo = page.timingInformation.get(firstDataObjectId)?.get(this.config.first.dataKey)
		const secondTimingInfo = page.timingInformation.get(secondDataObjectId)?.get(this.config.second.dataKey)
		const firstGapIndexes = firstTimingInfo?.gapIndexes ?? []
		const secondGapIndexes = secondTimingInfo?.gapIndexes ?? []
		const firstSamplingPeriod = firstTimingInfo?.samplingPeriod ?? Infinity
		const secondSamplingPeriod = secondTimingInfo?.samplingPeriod ?? Infinity
		const samplingPeriod = Math.max(firstSamplingPeriod, secondSamplingPeriod)

		// In the case of composite data, we also need to detect whether the value is NaN, to say whether it is a gap or not.
		const firstCombinedGapIndexes = [...new Set([...getGapIndexes(firstGapIndexes, this.config.first), ...firstNullIndexes])]
		const secondCombinedGapIndexes = [...new Set([...getGapIndexes(secondGapIndexes, this.config.second), ...secondNullIndexes])]
		const firstGapTimes = firstCombinedGapIndexes.map(index => [first.times[index - 1], first.times[index]])
		const secondGapTimes = secondCombinedGapIndexes.map(index => [second.times[index - 1], second.times[index]])

		const times: number[] = ([...new Set([...first.times, ...second.times])].filter(time => time !== undefined) as number[]).sort()

		if (times.length === 0) {
			return []
		}

		// We don't want to interpolate beyond where the data exists.
		const firstTimes = first.times.filter(time => time !== undefined) as number[]
		const secondTimes = second.times.filter(time => time !== undefined) as number[]

		// If there's prior data, add the gap fill from the previous page.
		const firstPreviousPageData = this.pageManager.getPreviousPage(page)?.data.get(firstDataObjectId)?.get(this.config.first.dataKey)
		const secondPreviousPageData = this.pageManager.getPreviousPage(page)?.data.get(secondDataObjectId)?.get(this.config.second.dataKey)

		if (firstPreviousPageData && secondPreviousPageData) {
			const firstPreviousSeriesData = getTimeSeriesData(firstPreviousPageData, this.config.first)
			const secondPreviousSeriesData = getTimeSeriesData(secondPreviousPageData, this.config.second)
			const time = Math.min(
				firstPreviousSeriesData.times[firstPreviousSeriesData.times.length - 1],
				secondPreviousSeriesData.times[secondPreviousSeriesData.times.length - 1],
			)

			if (firstPreviousSeriesData.data.length > 0 && secondPreviousSeriesData.data.length > 0) {
				const firstPrevValue = firstPreviousSeriesData.data[firstPreviousSeriesData.data.length - 1]
				const secondPrevValue = secondPreviousSeriesData.data[secondPreviousSeriesData.data.length - 1]

				if (!Number.isNaN(firstPrevValue) && !Number.isNaN(secondPrevValue) && times[0] - time <= 1.05 * samplingPeriod) {
					continuousChunks.push([[time, firstPrevValue, secondPrevValue]])
				}
			}
		}

		// Draw the interpolated area to the next non-gap timestamp
		for (const time of times) {
			if (continuousChunks.length === 0) {
				continuousChunks.push([])
			}

			if (
				(firstTimes.length > 0 && (time < firstTimes[0] || time > firstTimes[firstTimes.length - 1])) ||
				(secondTimes.length > 0 && (time < secondTimes[0] || time > secondTimes[secondTimes.length - 1]))
			) {
				if (continuousChunks[currentChunkIndex].length > 0) {
					continuousChunks.push([])
					currentChunkIndex++
				}

				continue
			}

			const firstHasGap = firstGapTimes?.some(([start, end]) => {
				return (
					(start !== undefined && end !== undefined && time > start && time <= end) ||
					(start !== undefined && end === undefined && time > start) ||
					(end !== undefined && start === undefined && time < end)
				)
			})

			const secondHasGap = secondGapTimes?.some(([start, end]) => {
				return (
					(start !== undefined && end !== undefined && time > start && time <= end) ||
					(start !== undefined && end === undefined && time > start) ||
					(end !== undefined && start === undefined && time < end)
				)
			})

			if (firstHasGap || secondHasGap) {
				continuousChunks.push([])
				currentChunkIndex++

				if (this.config.first.isCompositePart || this.config.second.isCompositePart) {
					// In the case of composite data, a gap usually means that we should skip the point.
					// This isn't entirely correct. Really, there should be a flag for whether the gap is a skip or not.
					// But for right now, the composite data that we do read is only from analytics, which are regularly sampled.
					continue
				}
			}

			continuousChunks[currentChunkIndex].push([time, firstDataInterpolator(time), secondDataInterpolator(time)])
		}

		const firstNextPageData = this.pageManager.getNextPage(page)?.data.get(firstDataObjectId)?.get(this.config.first.dataKey)
		const secondNextPageData = this.pageManager.getNextPage(page)?.data.get(secondDataObjectId)?.get(this.config.second.dataKey)

		// If there's data on the next page, add the gap fill to the next page
		if (firstNextPageData && secondNextPageData) {
			const firstNextSeriesData = getTimeSeriesData(firstNextPageData, this.config.first)
			const secondNextSeriesData = getTimeSeriesData(secondNextPageData, this.config.second)

			if (firstNextSeriesData.data.length > 0 && secondNextSeriesData.data.length > 0) {
				const firstNextValue = firstNextSeriesData.data[0]
				const secondNextValue = secondNextSeriesData.data[0]
				const time = Math.max(firstNextSeriesData.times[0], secondNextSeriesData.times[0])

				if (!Number.isNaN(firstNextValue) && !Number.isNaN(secondNextValue) && time - times[times.length - 1] <= 1.05 * samplingPeriod) {
					continuousChunks[currentChunkIndex].push([time, firstNextSeriesData.data[0], secondNextSeriesData.data[0]])
				}
			}
		}

		return continuousChunks
	}

	private checkIfOnlyOneValue(timeSeriesData: TimeSeriesData, page: ModalityPage): TimeSeriesData {
		if (timeSeriesData.data.length === 1) {
			return {
				data: new Float32Array([timeSeriesData.data[0], timeSeriesData.data[0]]),
				times: [page.startTime, page.endTime],
			}
		}

		return timeSeriesData
	}
}
